// Справка „Доставки": KPI (доставки · стойност · неплатени · стокови, които
// чакат фактура), търсачка, филтри В ЗАГЛАВИЯТА НА КОЛОНИТЕ (Тип · Доставчик ·
// Склад · Въвел · Фактура · Бруто от–до · Плащане) с ред „Филтри: …" над
// таблицата, сортиране по заглавията, Excel, документът при клик и менюто „⋯"
// на реда (Принтирай · Преглед · Редактирай · Анулирай; стокова във фактура —
// Принтирай · Преглед · Отвори фактурата). Периодът по
// подразбиране е „Този месец" (1-во → днес). Неплатените и стоковите се
// появяват, когато BE ги носи (иначе картата и колоната-филтър ги няма).
import { useEffect, useMemo, useState } from "react";
import {
  EuroOutlined,
  InboxOutlined,
  ProfileOutlined,
  WalletOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useViewport } from "../../../../hooks/useViewport";
import { useDeliveriesReport } from "../../../../hooks/useInventoryDeliveries";
import type { Delivery } from "../../../../api/inventory";
import type { RowAction } from "../../components/list/RowActionsMenu";
import { exportToExcel } from "../../../../utils/excelExport";
import { InventoryTheme } from "../../components/InventoryTheme";
import { PageHeader } from "../../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../../components/list/ListBits";
import { ReportToolbar } from "../../components/list/ReportToolbar";
import { refreshOf } from "../../components/list/reportRefresh";
import { periodOfPreset, periodToParams, type Period } from "../../components/period/periodPresets";
import { FilterSummaryLine } from "../../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../../components/filters/useColumnFilters";
import { useShowMore } from "../../components/list/useShowMore";
import { formatMoney } from "../../components/numbers";
import { useDeliveryDocument } from "../../delivery/components/useDeliveryDocument";
import {
  deliveryGrossOf,
  deliveryNetOf,
  docTypeOf,
  isDeliveryAnnulled,
  noteInvoiceStateOf,
  payKeyOf,
  warehouseNamesOf,
} from "../../delivery/components/deliveryMeta";
import { ReportLoading, ReportNoMatch, ShowMoreRow } from "../components/ReportListBits";
import {
  EMPTY_DELIVERY_FILTERS,
  deliveryFilterSpecs,
  deliveryKpis,
  searchDeliveries,
  sortDeliveries,
  sumGross,
  type DeliveryFilterKey,
  type DeliverySort,
  type DeliverySortKey,
} from "./deliveriesReportModel";
import { DeliveriesReportTable, type DeliveriesDensity } from "./DeliveriesReportTable";

export const DeliveriesReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow, isWide } = useViewport();
  const deliveryDocument = useDeliveryDocument();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<DeliverySort>({ key: "date", dir: "desc" });
  const deliveriesQuery = useDeliveriesReport({
    ...periodToParams(period),
    status: "all",
  });
  const { data: deliveries = [], isLoading } = deliveriesQuery;
  const { refresh, refreshing } = refreshOf(deliveriesQuery);

  // След запис на доставка: state.openDoc → документът (без действия)
  useEffect(() => {
    const state = location.state as { openDoc?: Delivery } | null;
    if (!state?.openDoc) return;
    deliveryDocument.open(state.openDoc, { editable: false });
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const typeLabel = (value: string) =>
    value === "note" ? t("inventoryPage.docTypeNote") : t("inventoryPage.docTypeInvoice");
  const invoiceStateLabel = (value: string) => t(`inventoryPage.invoiceState_${value}`);
  const payLabel = (value: string) =>
    ({
      unpaid: t("inventoryPage.payUnpaidMany"),
      paid: t("inventoryPage.payPaidMany"),
      byinv: t("inventoryPage.payByInvoice"),
      annulled: t("inventoryPage.payAnnulledMany"),
      untracked: t("inventoryPage.payUntracked"),
    })[value] ?? value;

  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const specs = useMemo(
    () =>
      deliveryFilterSpecs({
        titles: {
          types: t("inventoryPage.colDocType"),
          suppliers: t("inventoryPage.colSupplier"),
          warehouses: t("inventoryPage.colWarehouse"),
          users: t("inventoryPage.filterEnteredBy"),
          invoiceStates: t("inventoryPage.filterInvoiceState"),
          gross: t("inventoryPage.colGross"),
          pays: t("inventoryPage.colPayment"),
        },
        typeLabel,
        invoiceStateLabel,
        payLabel,
        formatMoney,
      }),
    // typeLabel/invoiceStateLabel/payLabel зависят само от t
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  const searched = useMemo(() => searchDeliveries(deliveries, query), [deliveries, query]);
  const filters = useColumnFilters({ specs, empty: EMPTY_DELIVERY_FILTERS, universe: deliveries, base: searched });
  const shown = useMemo(() => sortDeliveries(filters.rows, sort), [filters.rows, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, [period, filters.state, query, sort]);
  const kpis = deliveryKpis(deliveries);
  // „Изчисти филтрите" при празен резултат — и колоните, и търсачката
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };

  // Тип и Плащане — само когато BE ги носи
  const sections: ColumnSections<DeliveryFilterKey> = {
    types: kpis.typeKnown ? filters.section("types") : undefined,
    suppliers: filters.section("suppliers"),
    warehouses: filters.section("warehouses"),
    users: filters.section("users"),
    invoiceStates: kpis.typeKnown ? filters.section("invoiceStates") : undefined,
    gross: filters.section("gross"),
    pays: kpis.payKnown ? filters.section("pays") : undefined,
  };

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.kpiDeliveries"), value: String(kpis.count), Icon: InboxOutlined },
    { label: t("inventoryPage.kpiValueGross"), value: formatMoney(kpis.gross), Icon: EuroOutlined, tone: "brand" },
    ...(kpis.payKnown
      ? [{ label: t("inventoryPage.kpiUnpaid"), value: formatMoney(kpis.unpaid), Icon: WalletOutlined, tone: "danger" as const }]
      : []),
    ...(kpis.typeKnown
      ? [{ label: t("inventoryPage.kpiUninvoicedNotes"), value: String(kpis.uninvoicedNotes), Icon: FileTextOutlined }]
      : []),
  ];

  // Менюто „⋯" на реда — по шаблона на другите справки
  const actionsOf = (delivery: Delivery): RowAction[] => {
    const base: RowAction[] = [
      {
        key: "print",
        label: t("inventoryPage.print"),
        onClick: () => deliveryDocument.open(delivery, { autoPrint: true }),
      },
      {
        key: "view",
        label: t("inventoryPage.viewDocument"),
        onClick: () => deliveryDocument.open(delivery),
      },
    ];
    if (isDeliveryAnnulled(delivery)) return base;
    // стоковата във фактура не се редактира и не се анулира сама
    if (noteInvoiceStateOf(delivery) === "inInvoice")
      return [
        ...base,
        {
          key: "invoice",
          label: t("inventoryPage.openInvoice"),
          dividerBefore: true,
          onClick: () => void deliveryDocument.openById(delivery.invoiceId as number),
        },
      ];
    return [
      ...base,
      {
        key: "edit",
        label: t("inventoryPage.docEdit"),
        dividerBefore: true,
        onClick: () => deliveryDocument.edit(delivery),
      },
      {
        key: "annul",
        label: t("inventoryPage.docDelete"),
        danger: true,
        onClick: () => deliveryDocument.annul(delivery),
      },
    ];
  };

  const onSort = (key: DeliverySortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "supplier" || key === "doc" ? "asc" : "desc" },
    );

  const exportXls = () =>
    exportToExcel({
      filename: "dostavki",
      sheetName: t("inventoryPage.navDeliveriesReport"),
      rows: shown,
      columns: [
        { header: "№", value: (d) => d.id },
        { header: t("inventoryPage.colDate"), value: (d) => dayjs(d.date).format("DD.MM.YYYY") },
        { header: t("inventoryPage.colDocType"), value: (d) => typeLabel(docTypeOf(d)) },
        { header: t("inventoryPage.colDocument"), value: (d) => d.invoiceNumber ?? "" },
        { header: t("inventoryPage.colSupplier"), value: (d) => d.supplierName ?? "" },
        { header: t("inventoryPage.colWarehouse"), value: (d) => warehouseNamesOf(d).join(", ") },
        { header: t("inventoryPage.colNet"), value: (d) => deliveryNetOf(d) },
        { header: t("inventoryPage.colGross"), value: (d) => deliveryGrossOf(d) },
        { header: t("inventoryPage.colPayment"), value: (d) => payLabel(payKeyOf(d)) },
        {
          header: t("inventoryPage.filterInvoiceState"),
          value: (d) => {
            const state = noteInvoiceStateOf(d);
            return state ? invoiceStateLabel(state) : "";
          },
        },
        { header: t("inventoryPage.filterEnteredBy"), value: (d) => d.createdByName ?? "" },
        { header: t("inventoryPage.fieldNote"), value: (d) => d.note ?? "" },
        { header: t("inventoryPage.annulReason"), value: (d) => d.deleteReason ?? "" },
      ],
    });

  const density: DeliveriesDensity = isNarrow ? "cards" : isWide ? "full" : "medium";

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.navDeliveriesReport")}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.deliveriesSearchPh")} />
          </div>
          <SectionCard>
            <FilterSummaryLine
              countLabel={t("inventoryPage.deliveriesResult", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <span title={t("inventoryPage.deliveriesSumHint")}>
                {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(sumGross(shown))}</span>
              </span>
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : deliveries.length === 0 ? (
              <EmptyScreen Icon={ProfileOutlined} tone="brand" title={t("inventoryPage.deliveriesNone")} />
            ) : (
              // Празен резултат от филтрите — заглавията (с фуниите) остават
              <DeliveriesReportTable
                rows={visible}
                density={density}
                sort={sort}
                onSort={onSort}
                filters={sections}
                onOpen={(delivery) => deliveryDocument.open(delivery)}
                onPrint={(delivery) => deliveryDocument.open(delivery, { autoPrint: true })}
                actionsOf={actionsOf}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>
        {deliveryDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default DeliveriesReport;
